import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Autocomplete,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  Tooltip,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  LocationOn as LocationOnIcon,
  Edit as EditIcon,
  Map as MapIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as PendingIcon,
  Room as PositionedIcon,
  Delete as DeleteIcon,
  MyLocation as MyLocationIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  OpenInNew as OpenInNewIcon,
  Add as AddIcon,
  Build as BuildIcon
} from '@mui/icons-material';
import MapComponent from './MapComponent';
import { supabase } from '../../supabaseClient';
import proj4 from 'proj4';

const formatGeometryToString = (geoJson) => {
  if (!geoJson) return null;
  
  if (geoJson.type === 'Polygon') {
    // Handle both simple polygons and polygons with holes
    const allRings = geoJson.coordinates;
    return allRings.map(ring => ring.map(point => point.join(' ')).join(' ')).join(' | ');
  } else if (geoJson.type === 'MultiPolygon') {
    // For multi-polygon, we'll store as JSON string for now
    // This might need database schema changes for proper support
    return JSON.stringify(geoJson);
  }
  
  return null;
};

const parseGeometryFromString = (str) => {
  if (!str || typeof str !== 'string') return null;
  
  // Check if it's a JSON string (MultiPolygon)
  if (str.startsWith('{') || str.startsWith('[')) {
    try {
      return JSON.parse(str);
    } catch (e) {
      console.error("Invalid JSON geometry string:", str);
      return null;
    }
  }
  
  // Parse as polygon coordinates (potentially with holes)
  const rings = str.split(' | ');
  const coordinates = [];
  
  for (const ringStr of rings) {
    const coordsArray = ringStr.split(' ').map(s => parseFloat(s.trim())).filter(n => !isNaN(n));

    if (coordsArray.length === 0 || coordsArray.length % 2 !== 0) {
        console.error("Invalid ring geometry string:", ringStr);
        continue;
    }
    
    const ringCoords = [];
    for (let i = 0; i < coordsArray.length; i += 2) {
        ringCoords.push([coordsArray[i], coordsArray[i+1]]);
    }

    if (ringCoords.length >= 3) {
        coordinates.push(ringCoords);
    }
  }

  if (coordinates.length === 0) {
      console.error("No valid rings found in geometry string:", str);
      return null;
  }
  
  return {
      type: 'Polygon',
      coordinates: coordinates
  };
};

const DrawingTool = ({ document, onClose }) => {
  const [interactionMode, setInteractionMode] = useState('info');
  const [drawingMode, setDrawingMode] = useState('new-area'); // 'new-area', 'add-area', 'cut-hole'
  const [showCadastral, setShowCadastral] = useState(true);
  const [showHouseNumbers, setShowHouseNumbers] = useState(true);
  const [projectGeometry, setProjectGeometry] = useState(null);
  const [boreholes, setBoreholes] = useState([]);
  const [boreholeToDraw, setBoreholeToDraw] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isUpdatingGeometry, setIsUpdatingGeometry] = useState(false);
  const [editingBorehole, setEditingBorehole] = useState(null);
  const [editingGeometry, setEditingGeometry] = useState('');

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [mapCenter, setMapCenter] = useState(null);

  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfError, setPdfError] = useState(null);

  // Ref to avoid closure issues
  const boreholesToDrawRef = useRef(null);

  // Update ref when state changes
  useEffect(() => {
    boreholesToDrawRef.current = boreholeToDraw;
  }, [boreholeToDraw]);

  // Debounced search effect
  useEffect(() => {
    const searchLocation = async (query) => {
        if (!query || query.length < 3) {
            setSearchResults([]);
            return;
        }

        setIsSearching(true);
        try {
            const response = await fetch(
                `/api/bzk/locatieserver/search/v3_1/suggest?q=${encodeURIComponent(query)}&rows=5&fl=weergavenaam,id,score&fq=type:adres OR type:gemeente OR type:woonplaats OR type:weg`
            );
            
            if (response.ok) {
                const data = await response.json();
                setSearchResults(data.response?.docs || []);
            } else {
                setSearchResults([]);
            }
        } catch (error) {
            console.error('Search error:', error);
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    };

    const timeoutId = setTimeout(() => {
        if (searchQuery) {
            searchLocation(searchQuery);
        } else {
            setSearchResults([]);
        }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Handle selecting a location from search results
  const handleSelectLocation = async (locationId) => {
    try {
        const response = await fetch(
            `/api/bzk/locatieserver/search/v3_1/lookup?id=${locationId}&fl=centroide_ll`
        );
        
        if (response.ok) {
            const data = await response.json();
            const location = data.response?.docs?.[0];
            
            if (location?.centroide_ll) {
                const coordString = location.centroide_ll.replace('POINT(', '').replace(')', '');
                const [lon, lat] = coordString.split(' ').map(parseFloat);
                
                if (!isNaN(lon) && !isNaN(lat)) {
                    const rdCoords = proj4('EPSG:4326', 'EPSG:28992', [lon, lat]);
                    setMapCenter(rdCoords);
                } else {
                    console.error("Error parsing coordinates:", location.centroide_ll);
                }
            }
        }
    } catch (error) {
        console.error('Location lookup error:', error);
    }
    
    setSearchQuery('');
    setSearchResults([]);
  };

  // Load project data when document changes
  useEffect(() => {
    if (document) {
      loadProjectData();
    }
  }, [document]);

  useEffect(() => {
    const fetchFileUrl = async () => {
      try {
        if (!document?.storage_path) return;
        const { data, error: signedError } = await supabase.storage
          .from('project-documents')
          .createSignedUrl(document.storage_path, 3600);
        if (signedError) {
          setPdfError('Kan PDF niet ophalen');
          return;
        }
        if (data?.signedUrl) {
          setPdfUrl(data.signedUrl);
          setPdfError(null);
        }
      } catch (err) {
        setPdfError('Kan PDF niet ophalen');
      }
    };
    fetchFileUrl();
  }, [document]);

  async function loadProjectData() {
    if (!document) return;
    
    setLoading(true);
    try {
      // Load project data including geometry
      const { data: projectData } = await supabase
        .from('document_project')
        .select('id, geometry')
        .eq('document_id', document.id)
        .single();

      if (projectData) {
        // Load project geometry if it exists
        if (projectData.geometry) {
          const geometry = parseGeometryFromString(projectData.geometry);
          setProjectGeometry(geometry);
        }

        // Load boreholes via project
        const { data: boreholesData, error: boreholesError } = await supabase
          .from('document_borehole')
          .select('*')
          .eq('project_id', projectData.id)
          .order('name', { ascending: true });

        if (boreholesError) {
          console.error('Error loading boreholes:', boreholesError);
          setError('Fout bij laden van boorpunten');
        } else {
          setBoreholes(boreholesData || []);
        }
      }
    } catch (err) {
      console.error('Error loading project data:', err);
      setError('Fout bij laden van projectgegevens');
    } finally {
      setLoading(false);
    }
  }

  const handleProjectGeometryUpdate = async (newGeometry, mode = drawingMode) => {
    if (!document) return;
    
    setIsUpdatingGeometry(true);
    try {
      let finalGeometry = newGeometry;
      
      // Handle different drawing modes
      if (projectGeometry && mode !== 'new-area') {
        if (mode === 'add-area') {
          // Add as separate area (MultiPolygon)
          if (projectGeometry.type === 'MultiPolygon') {
            finalGeometry = {
              type: 'MultiPolygon',
              coordinates: [...projectGeometry.coordinates, newGeometry.coordinates]
            };
          } else if (projectGeometry.type === 'Polygon') {
            finalGeometry = {
              type: 'MultiPolygon',
              coordinates: [projectGeometry.coordinates, newGeometry.coordinates]
            };
          }
        } else if (mode === 'cut-hole') {
          // Add as hole in existing polygon
          if (projectGeometry.type === 'Polygon') {
            finalGeometry = {
              type: 'Polygon',
              coordinates: [...projectGeometry.coordinates, newGeometry.coordinates[0]]
            };
          } else {
            setError('Gaten kunnen alleen gemaakt worden in enkele gebieden, niet in meerdere losse gebieden');
            return;
          }
        }
      }
      
      const geometryString = formatGeometryToString(finalGeometry);
      
      // First get the project ID
      const { data: projectData } = await supabase
        .from('document_project')
        .select('id')
        .eq('document_id', document.id)
        .single();

      if (!projectData) {
        setError('Project niet gevonden');
        return;
      }
      
      const { error } = await supabase
        .from('document_project')
        .update({ geometry: geometryString })
        .eq('id', projectData.id);

      if (error) {
        console.error('Error updating project geometry:', error);
        setError('Fout bij opslaan van projectgeometrie');
      } else {
        setProjectGeometry(finalGeometry);
        
        // Success messages based on mode
        if (mode === 'add-area') {
          setSuccessMessage('Nieuw gebied toegevoegd');
        } else if (mode === 'cut-hole') {
          setSuccessMessage('Gat uitgesneden');
        } else {
          setSuccessMessage('Projectgeometrie opgeslagen');
        }
        
        setInteractionMode('info');
        setDrawingMode('new-area');
      }
    } catch (err) {
      console.error('Error updating geometry:', err);
      setError('Fout bij opslaan van projectgeometrie');
    } finally {
      setIsUpdatingGeometry(false);
    }
  };

  function handleDrawBoreholeStart(borehole) {
    setBoreholeToDraw(borehole);
    setInteractionMode('draw-borehole');
  }

  const handleBoreholeDrawn = async (coordinates) => {
    const currentBoreholeToDraw = boreholesToDrawRef.current;
    
    if (!currentBoreholeToDraw) {
      console.error('No borehole selected for drawing');
      return;
    }

    console.log('Saving borehole coordinates:', coordinates, 'for borehole:', currentBoreholeToDraw);
    setLoading(true);
    try {
      const geometryString = `${coordinates[0]} ${coordinates[1]}`;
      
      const { error } = await supabase
        .from('document_borehole')
        .update({ geometry: geometryString })
        .eq('id', currentBoreholeToDraw.id);

      if (error) {
        console.error('Error updating borehole:', error);
        setError('Fout bij opslaan van boorpunt');
      } else {
        // Update local state
        setBoreholes(prev => prev.map(b => 
          b.id === currentBoreholeToDraw.id 
            ? { ...b, geometry: geometryString }
            : b
        ));
        setSuccessMessage(`Boorpunt ${currentBoreholeToDraw.name} opgeslagen op coördinaten ${Math.round(coordinates[0])}, ${Math.round(coordinates[1])}`);
      }
    } catch (err) {
      console.error('Error saving borehole:', err);
      setError('Fout bij opslaan van boorpunt');
    } finally {
      setLoading(false);
      setBoreholeToDraw(null);
      setInteractionMode('info');
    }
  };

  const handleCancelBorehole = () => {
    setBoreholeToDraw(null);
    boreholesToDrawRef.current = null;
    setInteractionMode('info');
  };

  const handleDeleteProjectGeometry = async () => {
    if (!document) return;
    
    setIsUpdatingGeometry(true);
    try {
      // First get the project ID
      const { data: projectData } = await supabase
        .from('document_project')
        .select('id')
        .eq('document_id', document.id)
        .single();

      if (!projectData) {
        setError('Project niet gevonden');
        return;
      }
      
      const { error } = await supabase
        .from('document_project')
        .update({ geometry: null })
        .eq('id', projectData.id);

      if (error) {
        console.error('Error deleting project geometry:', error);
        setError('Fout bij verwijderen van projectgeometrie');
      } else {
        setProjectGeometry(null);
        setSuccessMessage('Projectgeometrie verwijderd');
        setInteractionMode('info');
      }
    } catch (err) {
      console.error('Error deleting geometry:', err);
      setError('Fout bij verwijderen van projectgeometrie');
    } finally {
      setIsUpdatingGeometry(false);
    }
  };

  const handleDeleteBoreholeGeometry = async (borehole) => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('document_borehole')
        .update({ geometry: null })
        .eq('id', borehole.id);

      if (error) {
        console.error('Error deleting borehole geometry:', error);
        setError('Fout bij verwijderen van boorpunt geometrie');
      } else {
        setBoreholes(prev => prev.map(b => 
          b.id === borehole.id 
            ? { ...b, geometry: null }
            : b
        ));
        setSuccessMessage('Boorpunt geometrie verwijderd');
      }
    } catch (err) {
      console.error('Error deleting borehole geometry:', err);
      setError('Fout bij verwijderen van boorpunt geometrie');
    } finally {
      setLoading(false);
    }
  };

  const handleEditGeometry = (borehole) => {
    setEditingBorehole(borehole);
    setEditingGeometry(borehole.geometry || '');
  };

  const handleSaveGeometry = async () => {
    if (!editingBorehole) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('document_borehole')
        .update({ geometry: editingGeometry.trim() || null })
        .eq('id', editingBorehole.id);

      if (error) {
        console.error('Error saving borehole geometry:', error);
        setError('Fout bij opslaan van boorpunt geometrie');
      } else {
        setBoreholes(prev => prev.map(b => 
          b.id === editingBorehole.id 
            ? { ...b, geometry: editingGeometry.trim() || null }
            : b
        ));
        setSuccessMessage('Boorpunt geometrie opgeslagen');
        setEditingBorehole(null);
        setEditingGeometry('');
      }
    } catch (err) {
      console.error('Error saving borehole geometry:', err);
      setError('Fout bij opslaan van boorpunt geometrie');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingBorehole(null);
    setEditingGeometry('');
  };

  const handleSelectBoreholeOnMap = (borehole) => {
    setBoreholeToDraw(borehole);
    setInteractionMode('select-borehole');
  };

  const getBoreholeStatus = (borehole) => {
    if (borehole.geometry && typeof borehole.geometry === 'string' && borehole.geometry.trim() !== '') {
      const parts = borehole.geometry.trim().split(/\s+/);
      if (parts.length === 2 && !isNaN(parseFloat(parts[0])) && !isNaN(parseFloat(parts[1]))) {
        return 'completed';
      }
      return 'positioned';
    }
    return 'pending';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'positioned':
        return <PositionedIcon color="warning" />;
      default:
        return <PendingIcon color="disabled" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return 'Ingetekend';
      case 'positioned':
        return 'Gepositioneerd';
      default:
        return 'Te intekenen';
    }
  };

  const handleOpenPdfInNewTab = () => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    }
  };

  // Enhanced geometry management
  const getGeometryDescription = (geometry) => {
    if (!geometry) return 'Geen geometrie';
    
    if (geometry.type === 'Polygon') {
      const ringCount = geometry.coordinates.length;
      const outerRing = geometry.coordinates[0];
      const holes = ringCount - 1;
      
      if (holes > 0) {
        return `Gebied met ${holes} ${holes === 1 ? 'gat' : 'gaten'} (${outerRing.length - 1} punten)`;
      } else {
        return `Enkele contour (${outerRing.length - 1} punten)`;
      }
    } else if (geometry.type === 'MultiPolygon') {
      const polygonCount = geometry.coordinates.length;
      const totalPoints = geometry.coordinates.reduce((sum, polygon) => sum + polygon[0].length - 1, 0);
      return `${polygonCount} losse gebieden (${totalPoints} punten totaal)`;
    }
    
    return 'Onbekend geometrie type';
  };

  const startDrawing = (mode) => {
    setDrawingMode(mode);
    setInteractionMode('draw-project-area');
  };

  return (
    <Box sx={{ 
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      bgcolor: 'background.default'
    }}>
      {/* Sidebar */}
      <Paper sx={{ 
        width: 400,
        height: '100%',
        overflow: 'auto',
        borderRadius: 0,
        borderRight: 1,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <Box sx={{ 
          p: 2, 
          borderBottom: 1, 
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="h6">
            Intekentool
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Open PDF in nieuw tabblad">
              <span>
                <IconButton onClick={handleOpenPdfInNewTab} size="small" disabled={!pdfUrl}>
                  <OpenInNewIcon />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        {/* Search */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" gutterBottom>
            Locatie zoeken
          </Typography>
          <Autocomplete
            size="small"
            options={searchResults}
            getOptionLabel={(option) => option.weergavenaam || ''}
            loading={isSearching}
            value={null}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            onInputChange={(event, newValue) => {
              setSearchQuery(newValue || '');
            }}
            onChange={(event, newValue) => {
              if (newValue) {
                handleSelectLocation(newValue.id);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Zoek adres, plaats..."
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <>
                      {isSearching ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.weergavenaam}
              </li>
            )}
            noOptionsText="Geen resultaten gevonden"
            loadingText="Zoeken..."
          />
        </Box>

        {/* Enhanced project geometry section */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" gutterBottom>
            Onderzoekslocatie
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              Status:
            </Typography>
            {isUpdatingGeometry ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                <Typography variant="body2">Opslaan...</Typography>
              </Box>
            ) : projectGeometry ? (
              <Chip icon={<CheckCircleIcon />} label="Ingetekend" color="success" size="small" />
            ) : (
              <Chip icon={<PendingIcon />} label="Nog niet ingetekend" color="default" size="small" />
            )}
          </Box>
          
          {projectGeometry && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {getGeometryDescription(projectGeometry)}
            </Typography>
          )}

          {/* Drawing mode selection */}
          {!projectGeometry ? (
            // First time drawing
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <Tooltip title="Teken eerste gebied">
                <span>
                  <IconButton
                    size="small"
                    color={interactionMode === 'draw-project-area' && drawingMode === 'new-area' ? 'primary' : 'default'}
                    onClick={() => startDrawing('new-area')}
                    disabled={isUpdatingGeometry}
                    variant="outlined"
                    sx={{ 
                      border: 1, 
                      borderColor: 'divider',
                      '&:hover': { borderColor: 'primary.main' }
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          ) : (
            // Additional drawing options
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <Tooltip title="Voeg los gebied toe">
                <span>
                  <IconButton
                    size="small"
                    color={interactionMode === 'draw-project-area' && drawingMode === 'add-area' ? 'primary' : 'default'}
                    onClick={() => startDrawing('add-area')}
                    disabled={isUpdatingGeometry}
                    sx={{ 
                      border: 1, 
                      borderColor: 'divider',
                      '&:hover': { borderColor: 'primary.main' }
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </span>
              </Tooltip>
              {projectGeometry.type === 'Polygon' && (
                <Tooltip title="Snijd gat uit">
                  <span>
                    <IconButton
                      size="small"
                      color={interactionMode === 'draw-project-area' && drawingMode === 'cut-hole' ? 'warning' : 'default'}
                      onClick={() => startDrawing('cut-hole')}
                      disabled={isUpdatingGeometry}
                      sx={{ 
                        border: 1, 
                        borderColor: 'divider',
                        '&:hover': { borderColor: 'warning.main' }
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Tooltip title="Kies perceel">
              <span>
                <IconButton
                  size="small"
                  color={interactionMode === 'select-project-plot' ? 'primary' : 'default'}
                  onClick={() => setInteractionMode('select-project-plot')}
                  disabled={isUpdatingGeometry}
                >
                  <MapIcon />
                </IconButton>
              </span>
            </Tooltip>
            {projectGeometry && (
              <>
                <Tooltip title="Bewerk contouren">
                  <span>
                    <IconButton
                      size="small"
                      color={interactionMode === 'modify-project-area' ? 'primary' : 'default'}
                      onClick={() => setInteractionMode('modify-project-area')}
                      disabled={isUpdatingGeometry}
                    >
                      <BuildIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Verwijder alle contouren">
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={handleDeleteProjectGeometry}
                      disabled={isUpdatingGeometry}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
          </Box>

          {/* Instructions based on interaction mode and drawing mode */}
          {interactionMode === 'draw-project-area' && (
            <Alert severity="info" sx={{ mt: 1 }}>
              {drawingMode === 'new-area' && 'Teken het eerste onderzoeksgebied op de kaart.'}
              {drawingMode === 'add-area' && 'Teken een nieuw los gebied dat wordt toegevoegd aan het onderzoek.'}
              {drawingMode === 'cut-hole' && 'Teken het gebied dat je wilt uitknippen (gat maken).'}
            </Alert>
          )}
          {interactionMode === 'select-project-plot' && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Klik op een perceel in de kadastrale kaart om de vorm over te nemen.
            </Alert>
          )}
          {interactionMode === 'modify-project-area' && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Klik op een contour om deze aan te passen. Sleep de hoekpunten.
            </Alert>
          )}
        </Box>

        {/* Enhanced boreholes section */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2">
              Boorpunten ({boreholes.length})
            </Typography>
            {boreholes.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                Klik op het locatie-icoon om een boorpunt in te tekenen
              </Typography>
            )}
          </Box>
          
          <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
            {loading ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <CircularProgress />
              </Box>
            ) : boreholes.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Geen boorpunten gevonden voor dit document
                </Typography>
              </Box>
            ) : (
              <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {boreholes.map((borehole) => {
                  const status = getBoreholeStatus(borehole);
                  const isEditing = editingBorehole?.id === borehole.id;
                  const isCurrentlyDrawing = boreholeToDraw?.id === borehole.id;
                  
                  return (
                    <Paper
                      key={borehole.id}
                      elevation={1}
                      sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: isCurrentlyDrawing ? 'primary.main' : 'divider',
                        backgroundColor: isCurrentlyDrawing ? 'primary.50' : 'inherit',
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: 2
                        }
                      }}
                    >
                      {/* Header - Name and Status */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <Box sx={{ mr: 1 }}>
                          {getStatusIcon(status)}
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {borehole.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {getStatusText(status)}
                            {isCurrentlyDrawing && ' - Klik op kaart'}
                          </Typography>
                        </Box>
                        <Chip 
                          size="small" 
                          label={status === 'completed' ? 'Compleet' : status === 'positioned' ? 'Gepositioneerd' : 'Te doen'}
                          color={status === 'completed' ? 'success' : status === 'positioned' ? 'warning' : 'default'}
                          variant={isCurrentlyDrawing ? 'filled' : 'outlined'}
                        />
                      </Box>

                      {/* Geometry Section */}
                      <Box sx={{ mb: 1.5 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Coördinaten
                        </Typography>
                        
                        {isEditing ? (
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <TextField
                              size="small"
                              value={editingGeometry}
                              onChange={(e) => setEditingGeometry(e.target.value)}
                              placeholder="X Y (bijv. 155000 463000)"
                              fullWidth
                              disabled={loading}
                              variant="outlined"
                            />
                            <Tooltip title="Opslaan">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={handleSaveGeometry}
                                disabled={loading}
                              >
                                <SaveIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Annuleren">
                              <IconButton
                                size="small"
                                onClick={handleCancelEdit}
                                disabled={loading}
                              >
                                <CancelIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                flexGrow: 1, 
                                fontFamily: 'monospace',
                                color: borehole.geometry ? 'text.primary' : 'text.secondary'
                              }}
                            >
                              {borehole.geometry || 'Niet ingesteld'}
                            </Typography>
                            <Tooltip title="Bewerk coördinaten">
                              <IconButton
                                size="small"
                                onClick={() => handleEditGeometry(borehole)}
                                disabled={loading}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Klik op kaart">
                              <IconButton
                                size="small"
                                onClick={() => handleSelectBoreholeOnMap(borehole)}
                                disabled={loading}
                              >
                                <MyLocationIcon />
                              </IconButton>
                            </Tooltip>
                            {borehole.geometry && (
                              <Tooltip title="Verwijder coördinaten">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteBoreholeGeometry(borehole)}
                                  disabled={loading}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        )}
                      </Box>

                      {/* Cancel drawing button when actively drawing */}
                      {isCurrentlyDrawing && (
                        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                          <Tooltip title="Annuleer intekenen">
                            <IconButton
                              size="small"
                              onClick={handleCancelBorehole}
                              color="secondary"
                              sx={{ 
                                border: 1, 
                                borderColor: 'divider',
                                '&:hover': { borderColor: 'secondary.main' }
                              }}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Paper>
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Map */}
      <Box sx={{ flexGrow: 1, height: '100%' }}>
        <MapComponent
          interactionMode={interactionMode}
          drawingMode={drawingMode}
          showCadastral={showCadastral}
          showHouseNumbers={showHouseNumbers}
          onLayerChange={{ 
            setCadastral: setShowCadastral, 
            setHouseNumbers: setShowHouseNumbers 
          }}
          boreholes={boreholes}
          boreholeToDraw={boreholeToDraw}
          onBoreholeDrawn={handleBoreholeDrawn}
          onCancelBorehole={handleCancelBorehole}
          onBoreholeSelected={(coordinates) => {
            if (boreholeToDraw && interactionMode === 'select-borehole') {
              handleBoreholeDrawn(coordinates);
            }
          }}
          centerOn={mapCenter}
          projectGeometry={projectGeometry}
          onProjectGeometryUpdate={handleProjectGeometryUpdate}
        />
      </Box>

      {/* Snackbars for messages */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DrawingTool; 